import { CheckIcon, EnvelopeIcon, XMarkIcon } from '@heroicons/react/16/solid';
import { useMemo } from 'react';

import { format } from 'date-fns';
import { useAuthStore } from 'src/entities/user/store/useAuthStore';
import { FileAttachment, UserAvatar } from 'src/shared/components';
import { Question } from 'src/shared/types/question';
import { CustomFile } from 'src/shared/types/update';

import ImageGallery from 'src/shared/components/ImageGallery';
import { Tag, Typography } from 'src/shared/components/ui';

import findUserInOrganization from '../../utils/findUserInOrganization';
import isFileImage from '../../utils/isFileImage';
import MarkdownContent from './markdown-content/MarkdownContent';

const UpdateAnswerContent = ({ question }: { question: Question }) => {
  const answer = question.answer;
  const answeredAt = question?.answered_at;
  const answeredBy = question?.answered_by?.id;
  // An approval answered in the app is [decision, reason] - v1 structure.
  // Approvals answered by email before email replies became thread replies
  // stored the email's text, which carries no decision.
  const approvalAnswer =
    question.question_type === 'approval' && Array.isArray(answer)
      ? answer
      : null;

  const selectedOrganization = useAuthStore(
    (state) => state.selectedOrganization
  );

  const answeredByUser = answeredBy
    ? findUserInOrganization(selectedOrganization, answeredBy)
    : null;

  const renderAnswer = () => {
    if (approvalAnswer) {
      return approvalAnswer[1] ?? '';
    }

    if (Array.isArray(answer)) {
      return (answer || '').join(', ').trim();
    }

    return (answer || '').trim();
  };

  const renderApprovalTag = () => {
    if (approvalAnswer?.[0] === 'approved') {
      return (
        <Tag size="sm" color="flat">
          <div className="flex items-center gap-[2px]">
            <CheckIcon className="size-4" /> Approved
          </div>
        </Tag>
      );
    }

    return (
      <Tag size="sm" color="red" variant="flat">
        <div className="flex items-center gap-[2px]">
          <XMarkIcon className="size-4" /> Rejected
        </div>
      </Tag>
    );
  };

  const imageUrls = useMemo(() => {
    return question?.files?.filter(isFileImage) ?? [];
  }, [question?.files]);

  const files = useMemo(() => {
    return question?.files?.filter((file) => !isFileImage(file)) ?? [];
  }, [question?.files]);

  const withAttachments = useMemo(() => {
    if (question?.files) {
      return question?.files?.length > 0;
    }

    return false;
  }, [question?.files]);

  const renderFiles = () => {
    return files.map((file: CustomFile) => (
      <FileAttachment key={file.id} fileName={file.name} fileUrl={file.url} />
    ));
  };

  return (
    <div className="relative flex gap-2 items-start mt-6 py-2">
      <div
        style={{ rotate: '180deg' }}
        className="w-[40px] h-[40px] top-[-8px] left-[-38px] absolute z-10"
      >
        <svg width="40" height="40" viewBox="0 0 40 40">
          <path
            d="M0,0 A40,40 0 0,1 40,40"
            stroke="#dee2e6"
            strokeWidth="1"
            fill="none"
            strokeLinecap="round"
          />
        </svg>
      </div>
      <div className="border-3 border-white rounded-full">
        <UserAvatar
          image={answeredByUser?.avatar_url ?? ''}
          name={answeredByUser?.username || 'user profile'}
          disableAnimation
          withTooltip
        />
      </div>

      <div className="mt-2 flex flex-col w-full">
        <div className="flex-1 flex-col flex gap-[2px]">
          <div className="flex gap-2 w-full items-center">
            <MarkdownContent markdownString={renderAnswer()} />
            {approvalAnswer && (
              <>
                <div className="size-1 bg-gray-4 rounded-full" />
                {renderApprovalTag()}
              </>
            )}
          </div>
          {withAttachments && (
            <div className="flex gap-2 mt-1 items-center">
              {imageUrls.length > 0 && <ImageGallery imageUrls={imageUrls} />}
              {files.length > 0 && renderFiles()}
            </div>
          )}

          {answeredAt && (
            <div className="flex items-center gap-1 mt-1">
              {question.reply_source?.sendgrid_id && (
                <EnvelopeIcon
                  className="h-4 w-4 text-secondary-text"
                  title="Received via email"
                />
              )}
              <Typography variant="body-2" className="text-secondary-text">
                {format(answeredAt, 'MMM dd, yyyy')}
              </Typography>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UpdateAnswerContent;
